import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  AnalyticsService,
  AuthorizationService,
  ToastrService,
  ExportService,
  LoggingService,
  AnnotationsSentData,
  AnnotationSentData,
} from "app/shared";
import { BaseChartLogic } from "app/analytics/BaseChartLogic";
import { AnnotationsSentConfig } from "app/shared/model/charts/user-analytic-configurations.model";

@Component({
  selector: "annotations-chart",
  templateUrl: "../../common-chart-template.component.html",
  standalone: false,
})
export class AnnotationsChartComponent
  extends BaseChartLogic<AnnotationsSentConfig, AnnotationsSentData>
  implements OnInit, OnDestroy
{
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() showTitle: boolean = true;
  @ViewChild("chartCanvas") chartCanvas!: ElementRef<HTMLCanvasElement>;

  constructor(
    _analyticsService: AnalyticsService,
    _authService: AuthorizationService,
    _exportService: ExportService,
    _toastrService: ToastrService,
    _loggingService: LoggingService,
    _ngZone: NgZone
  ) {
    super(
      _analyticsService,
      _authService,
      _exportService,
      _toastrService,
      _loggingService,
      AnnotationsSentConfig,
      _ngZone
    );
    this.handleDataSubscription(
      this.analyticsService.annotationsSentDataChange
    );
  }

  getCustomLabel(dataPoint: AnnotationSentData, label: string): any {
    if (label !== "User Data" && label !== "Annotations") {
      return "";
    }

    const users = dataPoint.SentByUsers;
    const dictionary = dataPoint.UserIdToDatesDict;

    if (label === "User Data") {
      //build out objects of users and dates printed
      // Use all options if customLabelSelection is undefined or empty
      const customLabelSelection = this.config?.customLabelSelection[
        "User Data"
      ]?.length
        ? this.config.customLabelSelection["User Data"]
        : this.chartDetails.customLabels["User Data"];

      const result_asTable: any[] = [];

      users.forEach((user) => {
        const userDates = dictionary[user.Id] || [];

        const row = {};
        if (customLabelSelection.includes("User.FullName")) {
          row["FullName"] = `${user.FirstName} ${user.LastName}`;
        }

        if (customLabelSelection.includes("User.Email")) {
          row["Email"] = user.UserName;
        }

        if (customLabelSelection.includes("Date Sent")) {
          row["Date Sent"] = userDates
            .map((date) => new Date(date).toLocaleString())
            .join("; ")
            .replace(",", "-");
        }

        if (customLabelSelection.includes("Total")) {
          row["TotalByUser"] = userDates.length;
        }

        result_asTable.push(row);
      });
      return result_asTable;
    }

    if (label === "Annotations") {
      const result_asTable: any[] = [];
      dataPoint.Annotations.forEach((annotation) => {
        const row = {};
        var userId = Number(annotation.UserId);
        var user = users.find((u) => u.Id === userId);
        row["User"] = `${user.FirstName} ${user.LastName}`;
        row["Email"] = user.UserName;
        row["Note"] = annotation.Note;
        row["Submitted"] = new Date(annotation.Submitted).toLocaleString();
        row["__action_buttons"] = {
          actionComponent: "annotation_buttons",
          annotationId: annotation.Id,
          documentId: annotation.DocumentId,
          isPDF: annotation.IsPDF,
        };

        result_asTable.push(row);
      });
      return result_asTable;
    }
  }

  dataPointKey: string = "Total";
  protected getDataPointDisplayValue(dataPoint: AnnotationSentData): any {
    return dataPoint.Total;
  }

  buildFilterSettings(): void {
    this.addPeriodtoFilterSettings();
    this.addStartEndDatesToFilterSettings(this.startDate, this.endDate);
  }

  refreshData(): void {
    this.analyticsService.getAnnotationsSentData(this.filterSettings);
  }

  ngOnInit(): void {
    super.init();
  }

  ngOnDestroy(): void {
    super.cleanup();
  }
}
